// Firebase services for storing form submissions and bookings
import { collection, addDoc, serverTimestamp, runTransaction, query, where, getDocs, doc } from 'firebase/firestore';
import { db } from './config';

/**
 * Add a subscriber to the email list (from meditation form)
 */
export const addSubscriber = async (name, email) => {
  try {
    // Check for duplicate email
    // DIRECT ADD (Bypassing duplicate check to fix permission errors in client-side code)
    // We cannot query 'subscribers' from the client because of security rules.
    const docRef = await addDoc(collection(db, 'subscribers'), {
      name: name.trim(),
      email: email.trim().toLowerCase(),
      source: 'meditation_form',
      createdAt: serverTimestamp(),
      status: 'active'
    });

    const subscriberId = docRef.id;
    const isDuplicate = false; // We can't check for duplicates client-side anymore

    // Trigger welcome email sequence (even for duplicates - they might want to receive it again)
    try {
      const { triggerEmailSequence } = await import('./email');
      await triggerEmailSequence('welcome', {
        email: email.trim().toLowerCase(),
        name: name.trim(),
        variables: {}
      });
    } catch (error) {
      console.error('Error triggering welcome sequence:', error);
      // Don't fail the subscription if email sequence fails
    }

    return { success: true, id: subscriberId, duplicate: isDuplicate };
  } catch (error) {
    console.error('Error adding subscriber: ', error);
    return { success: false, error: error.message };
  }
};

/**
 * Add a booking to Firestore with transaction to prevent double bookings
 */
export const addBooking = async (bookingData) => {
  try {
    // Convert date to YYYY-MM-DD format for consistency
    let dateStr;
    if (bookingData.date instanceof Date) {
      dateStr = bookingData.date.toISOString().split('T')[0];
    } else if (typeof bookingData.date === 'string') {
      dateStr = bookingData.date.split('T')[0];
    } else {
      dateStr = new Date(bookingData.date).toISOString().split('T')[0];
    }

    // Use transaction to ensure atomic booking (prevents race conditions)
    const result = await runTransaction(db, async (transaction) => {
      const bookingsRef = collection(db, 'bookings');

      // Check if slot is still available
      const q = query(
        bookingsRef,
        where('date', '==', dateStr),
        where('time', '==', bookingData.time),
        where('status', 'in', ['pending', 'confirmed'])
      );

      const existingBookings = await getDocs(q);

      if (!existingBookings.empty) {
        throw new Error('This time slot is no longer available');
      }

      // Create booking
      const newBookingRef = doc(collection(db, 'bookings'));
      transaction.set(newBookingRef, {
        name: bookingData.name.trim(),
        email: bookingData.email.trim().toLowerCase(),
        date: dateStr,
        time: bookingData.time,
        notes: (bookingData.notes || '').trim(),
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return { id: newBookingRef.id };
    });

    console.log('Booking added with ID: ', result.id);

    // Trigger booking confirmed email sequence
    try {
      const { triggerEmailSequence } = await import('./email');
      await triggerEmailSequence('booking-confirmed', {
        email: bookingData.email.trim().toLowerCase(),
        name: bookingData.name.trim(),
        variables: {
          bookingDate: dateStr,
          bookingTime: bookingData.time
        }
      });
    } catch (error) {
      console.error('Error triggering booking sequence:', error);
      // Don't fail the booking if email sequence fails
    }

    return { success: true, id: result.id };
  } catch (error) {
    console.error('Error adding booking:', error);
    return {
      success: false,
      error: error.message || 'Failed to create booking. Please try again.'
    };
  }
};
